import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@brent/core-ui/ui'
import type { SurveyResults as SurveyResultsType } from '@/services/api/surveys'

const LIKERT_LABELS: Record<number, string> = {
  1: 'Strongly Disagree',
  2: 'Disagree',
  3: 'Neutral',
  4: 'Agree',
  5: 'Strongly Agree',
}

interface SurveyResultsProps {
  results: SurveyResultsType
  questionIds?: string[]
}

export default function SurveyResults({ results, questionIds }: SurveyResultsProps) {
  const qIds = questionIds ?? results.likert_results.map((r) => r.question_id)

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Results ({results.total_responses} response{results.total_responses !== 1 ? 's' : ''})
          </CardTitle>
        </CardHeader>
      </Card>

      {results.likert_results.map((question) => {
        const maxCount = Math.max(...Object.values(question.distribution), 1)

        return (
          <Card key={question.question_id}>
            <CardHeader>
              <CardTitle className="text-base">{question.question_text}</CardTitle>
              <p className="text-sm text-muted-foreground">
                Average: {question.average.toFixed(2)}
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((rating) => {
                  const count = question.distribution[rating] ?? 0
                  const percentage =
                    results.total_responses > 0
                      ? Math.round((count / results.total_responses) * 100)
                      : 0
                  const barWidth = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0

                  return (
                    <div key={rating} className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground w-36 flex-shrink-0">
                        {LIKERT_LABELS[rating]}
                      </span>
                      <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                        <div
                          className="bg-primary h-full rounded-full transition-all"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                      <span className="text-sm w-16 text-right">
                        {count} ({percentage}%)
                      </span>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )
      })}

      {results.individual_responses.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Individual Responses</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  {qIds.map((qId, i) => (
                    <TableHead key={qId} className="w-12 text-center">
                      Q{i + 1}
                    </TableHead>
                  ))}
                  <TableHead>Free Text</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.individual_responses.map((response) => (
                  <TableRow key={response.user_id}>
                    <TableCell className="font-medium text-sm">
                      {response.user_name || response.user_email || response.user_id}
                    </TableCell>
                    {qIds.map((qId) => (
                      <TableCell key={qId} className="text-center text-sm">
                        {response.likert_answers[qId] ?? '-'}
                      </TableCell>
                    ))}
                    <TableCell className="text-sm max-w-xs truncate">
                      {response.free_text_answer}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
